import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AtiraTabs } from '../../components/AtiraTabs';
import { Button } from '../../components/Button';
import { Flex } from '../../components/Flex';
import { UserHeader } from '../../components/UserHeader';
import { urlTestIDs } from '../../constants/test/url.testIds';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { shortenUrlActions } from '../../redux/url/url.slice';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { Breakpoints } from '../../theme/Breakpoints';
import { Rounded } from '../../theme/Rounded';
import { Shadow } from '../../theme/Shadow';
import { Spacing } from '../../theme/Spacing';
import { URLShortnerModal } from './URLShortnerModal';
import { UserURLs } from './UserURLs';

const HeaderWrapper = styled(Flex)`
  display: none;
  margin-top: ${Spacing.s};

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
  }
`;

const StyledButton = styled(Button)`
  position: fixed;
  bottom: ${Spacing.xl};
  inset-inline-end: ${Spacing.xl};
  width: 3.5rem;
  aspect-ratio: 1 / 1;
  border-radius: ${Rounded.circle};
  box-shadow: ${Shadow.MD};
  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

export const URLRoute: React.FC = () => {
  const [shortenModalVisible, setShortenModalVisible] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useDocumentTitle(`${t('app.name').toUpperCase()} - ${t('common.my_urls')}`);

  const loggedInUserId = useAppSelector(
    userSliceSelectors.selectLoggedInUserId,
  )!;

  useEffect(() => {
    dispatch(shortenUrlActions.getUserShortURLs({ userId: loggedInUserId }));
  }, [dispatch, loggedInUserId]);

  useEffect(() => {
    const isNavigatedToCreate = location.state?.isNavigatedToCreate;

    if (isNavigatedToCreate) {
      setShortenModalVisible(true);
      navigate(location, { replace: true });
    }
  }, [location, navigate]);

  return (
    <Fragment>
      <AtiraTabs
        items={[
          {
            key: 'USER_URLS',
            label: t('side_menu.my_urls'),
            children: <UserURLs />,
          },
        ]}
        sideComponent={
          <HeaderWrapper>
            <UserHeader />
          </HeaderWrapper>
        }
      />

      <URLShortnerModal
        open={shortenModalVisible}
        onClose={() => setShortenModalVisible(false)}
      />

      <StyledButton
        icon={faPlus}
        dataTestId={urlTestIDs.shorten.modalButton}
        onClick={() => setShortenModalVisible(true)}
      />
    </Fragment>
  );
};
